import { withApiHost } from "./config";
import { apiFetch } from "./fetch-api";
import type { UserState } from "@/types/user";
import type { OrgGroupItem } from "@/types/user";
import { hasInternalDomainAccount } from "@/lib/user-access";
import { getUserAvatarSrc } from "@/lib/brand";

const roleAdmin = "bind360";
const roleGuest = "guest";

function formatUserRole(role: string | undefined): string {
  if (!role) return roleGuest;
  if (role === "1" || role === "admin" || role === "bind360") return roleAdmin;
  return roleGuest;
}

function formatOrgRoleName(role: string | number | undefined): string {
  const roleMap = ["无", "所有者", "管理员", "普通成员"];
  const roleNumber = Number.parseInt(String(role ?? 0), 10);
  return roleMap[roleNumber] || "成员";
}

/** 获取当前用户信息（依赖当前品牌登录 Cookie） */
export async function getAccountInfo(): Promise<{
  data: Record<string, unknown>;
  stat?: number;
}> {
  const url = withApiHost("/manage_api/uinfo");
  const channel =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("channel")
      : null;
  const requestUrl = channel
    ? `${url}${url.includes("?") ? "&" : "?"}channel_id=${encodeURIComponent(channel)}`
    : url;
  const res = await apiFetch(requestUrl, { cache: "no-store", toast: { error: false }, skipAuthRedirect: true });
  if (!res.ok) {
    throw new Error(`uinfo 请求失败: ${res.status}`);
  }
  const json = await res.json();
  return json as { data: Record<string, unknown>; stat?: number };
}

/** 获取组织/群组列表 */
export async function getOrgList(): Promise<{ data?: OrgGroupItem[] }> {
  const url = withApiHost("/manage_api/org/list");
  const res = await apiFetch(url, { cache: "no-store", toast: { error: false }, skipAuthRedirect: true });
  if (!res.ok) {
    throw new Error(`org/list 请求失败: ${res.status}`);
  }
  const json = await res.json();
  return json as { data?: OrgGroupItem[] };
}

/**
 * 拉取用户信息 + 组织列表，合并为 UserState（与前置 info 逻辑一致）
 */
export async function fetchUserInfo(): Promise<UserState> {
  const res: { data?: Record<string, unknown>; stat?: number } =
    await getAccountInfo();
  const { data: groupRes } = await getOrgList();
  const data = res.data;

  if (data?.stat !== 1 || !groupRes || !groupRes.length) {
    throw new Error("NO_INFO_WHITE_LIST");
  }

  const list = Array.isArray(groupRes) ? groupRes : [];
  list
    .filter((item: OrgGroupItem) => item.status === 1)
    .forEach((g) => {
      g.role_name = formatOrgRoleName(g.role);
    });

  if (list.length === 0) {
    throw new Error("NO_INFO_WHITE_LIST");
  }

  const baseUser = {
    bind_360: data.bind_360 as string | undefined,
    is_intr: data.is_intr as number | undefined,
  };
  const hasInternalAccount = hasInternalDomainAccount(baseUser);

  const userData: UserState = {
    phone: (data.phone as string) ?? "",
    company: (data.company as string) ?? "",
    name: (data.user_name as string) ?? "",
    description: (data.description as string) ?? "",
    qid: (data.qid as string) ?? "",
    login_name: (data.login_name as string) ?? "",
    email: (data.login_email as string) ?? "",
    login_phone: (data.login_phone as string) ?? "",
    stat: (data.stat as number) ?? 0,
    tokens: data.tokens as number | undefined,
    amount: data.amount as number | undefined,
    force_limit: data.force_limit as number | undefined,
    warn_limit: data.warn_limit as number | undefined,
    apply_limit: data.apply_limit as number | undefined,
    bind_360: baseUser.bind_360,
    point: data.p as number | undefined,
    is_intr: baseUser.is_intr,
    has_internal_domain_account: hasInternalAccount,
    avatar: getUserAvatarSrc(
      (data.image_url as string)
        ?.replace("48_48_80", "120_120_100")
        .replace("http://", "https://"),
    ),
    role: hasInternalAccount
      ? formatUserRole("bind360")
      : formatUserRole("guest"),
    department: (data.department as string) ?? "",
    allJoinedGroups: list,
  };

  return userData;
}

/**
 * 获取组织成员列表
 */
export interface GroupMember {
  qid: string;
  name: string;
  login_name: string;
  bind_360?: string | boolean;
  status: number;
  role?: string;
  [key: string]: unknown;
}

export async function getGroupMembers(orgId: string): Promise<GroupMember[]> {
  const url = withApiHost(`/manage_api/org/${orgId}/members`);
  const res = await apiFetch(url, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`获取组织成员失败: ${res.status}`);
  }
  const json = await res.json();
  // 根据实际API响应格式调整，假设返回 { data: [...] } 的格式
  const data = (json as { data?: GroupMember[] }).data || [];
  return Array.isArray(data) ? data : [];
}

export interface Bind360Params {
  sid: string;
  ref: string;
}

interface Bind360Response {
  errno?: number;
  message?: string;
}

export async function bind360Account(params: Bind360Params): Promise<void> {
  const url = withApiHost("/manage_api/bind_360");
  const res = await apiFetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  });

  let payload: Bind360Response | null = null;
  try {
    payload = (await res.json()) as Bind360Response;
  } catch {
    payload = null;
  }

  const errno = typeof payload?.errno === "number" ? payload.errno : undefined;
  const message = payload?.message || `绑定域账户失败: ${res.status}`;

  if (!res.ok || (typeof errno === "number" && errno !== 0)) {
    throw new Error(message);
  }
}
